import { FARM_UNLOCK_LEVEL } from 'Constants/Farm';
import Game from 'Game';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import ContextStateHelper from 'State/Game/Context/ContextStateHelper';
import { ContextType } from 'State/Game/ContextType';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { IGameState } from 'State/Game/IGameState';
import { PopupID } from 'State/UI/PopupID';
import UIStateHelper from 'State/UI/UIStateHelper';
import TimerStateHelper from 'State/Timer/TimerStateHelper';
import { FarmItemUnlockType } from 'System/Farm/Data/FarmItemUnlockType';
import { FarmSlotType } from 'System/Farm/Data/FarmSlotType';
import {
    AreaData,
    getFarmItemsByField,
    getFarmItemsBySlotType,
    parseFarmSlotId,
    trackNewItemPlaced,
} from 'System/Farm/FarmHelpers';
import { isShopSupported } from 'Utility/Platform';
import { IAPReward, IRewardData } from 'Utility/RewardHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import { TActionType, TransactionType } from 'Utility/Tracking/DataSet/Transaction';
import { TimerType } from 'System/Timer/TimerTypes';
import { trackResourceEvent } from 'Utility/Tracking/TrackingHelpers/Transaction';
import { InventoryStateHelper } from '../Inventory/InventoryStateHelper';
import { IPlayerState } from '../IPlayerState';
import { PetsStateHelper } from '../Pets/PetsStateHelper';
import { getHighestLevelCompleted } from '../PlayerLevelHelper';
import { PlayerStateHelper } from '../PlayerStateHelper';
import { PlayerTask } from '../PlayerTasks';
import { FarmAreaId, FarmSlotId, IFarmPlayerState } from './IFarmPlayerState';
import { initialFarmPlayerState } from './initialFarmPlayerState';
import DateHelper from 'Utility/DateHelper';
import { ProductId } from 'Utility/Product';
import { IAPStateHelper } from 'State/Player/IAP/IAPStateHelper';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { CACHED_FARM_ITEM_DATA, FarmItemData, FarmItemId, getFarmItemById } from 'System/Farm/Data/FarmItemData';
import { FarmCatalogueTab } from 'System/UI/Menu/FarmCatalogueTab';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { getIAPRewards } from 'Utility/Helpers/IAP/IAPRewards';

const { getFieldNameFromId } = WorldDataHelper;

export namespace FarmStateHelper {
    import getAllFieldData = WorldDataHelper.getAllFieldData;

    export function setFarmCatalogueTab(state: IGameState, tab: FarmCatalogueTab): IGameState {
        // Active slot ID is tracked in the UI state - should probably be added to `state.farmState`.
        const { type: slotType } = parseFarmSlotId(state.UIState.params.slotId);

        return {
            ...state,
            farmState: {
                ...state.farmState,
                activeTab: tab,
            },
            playerState: {
                ...state.playerState,
                decoItemsSeen: [
                    ...state.playerState.decoItemsSeen,

                    // Append any new, unlocked items that are not already in `decoItemsSeen` for the specified tab.
                    ...FarmStateHelper.filterUnlockedFarmItems(
                        state,
                        FarmStateHelper.getFarmItemIdsForTab(tab, slotType),
                        false,
                        true
                    ).filter(itemId => !state.playerState.decoItemsSeen.includes(itemId)),
                ],
            },
        };
    }

    export function cancelSlotItem(state: IGameState): IGameState {
        state = hideFarmCatalogue(state);
        return FarmStateHelper.clearFarmSlotPreviews(state);
    }

    function hideFarmCatalogue(state: IGameState): IGameState {
        return {
            ...state,
            UIState: UIStateHelper.hidePopup(state.UIState, PopupID.FARM_CATALOGUE),
        };
    }

    export function confirmSlotItem(state: IGameState, slotId: FarmSlotId, itemId: FarmItemId): IGameState {
        // Get current slot item as "previous" slot to compare for tracking purposes.
        const previousSlotItemId = state.playerState.farmState.slots[slotId]?.itemId;

        state = hideFarmCatalogue(state);
        state = confirmFarmSlotItem(state, slotId, itemId);

        trackNewItemPlaced(state, slotId, previousSlotItemId, itemId);

        return state;
    }

    export function isFarmFeatureUnlocked(state: IGameState): boolean {
        return getHighestLevelCompleted(state.playerState) >= FARM_UNLOCK_LEVEL;
    }

    export function queueFarmBtnFTUXIfNeeded(state: IGameState): IGameState {
        if (
            isFarmFeatureUnlocked(state) &&
            !FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.FARM_BTN) &&
            state.playerState.tasks.find(task => task.task === PlayerTask.FARM_BTN_FTUX) === undefined
        ) {
            return {
                ...state,
                playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                    {
                        task: PlayerTask.FARM_BTN_FTUX,
                    },
                ]),
            };
        }
        return state;
    }

    export function queueFarmFTUXIfNeeded(state: IGameState): IGameState {
        if (isFarmFeatureUnlocked(state)) {
            if (!FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.FARM)) {
                state = {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.FARM_FTUX,
                        },
                    ]),
                };
            }

            if (
                PetsStateHelper.isAnyPetUnlocked(state.playerState.petsState) &&
                !FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.FARM_PET)
            ) {
                state = {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.FARM_PET_FTUX,
                        },
                    ]),
                };

                state = TimerStateHelper.setTimer(state, TimerType.FARM_MINI_GAME, null);
            }
        }

        return state;
    }

    export function confirmFarmSlotItem(state: IGameState, slotId: FarmSlotId, itemId: FarmItemId): IGameState {
        // Only confirm the item and play the animation if the item ID has changed.
        const isConfirmed = state.playerState.farmState.slots[slotId]?.itemId !== itemId;

        return {
            ...state,
            farmState: {
                ...state.farmState,
                previewSlots: {
                    ...state.farmState.previewSlots,
                    [slotId]: {
                        ...state.farmState.previewSlots[slotId],
                        // Clear preview item ID.
                        previewItemId: undefined,

                        // Set isConfirmed for confirm-animation.
                        isConfirmed,
                    },
                },
            },
            playerState: {
                ...state.playerState,
                farmState: {
                    ...state.playerState.farmState,
                    slots: {
                        ...state.playerState.farmState.slots,
                        [slotId]: {
                            ...state.playerState.farmState.slots[slotId],
                            // Set the new item in player-data.
                            itemId,
                        },
                    },
                },
            },
        };
    }

    export function clearFarmSlotPreviews(state: IGameState): IGameState {
        return {
            ...state,
            farmState: {
                ...state.farmState,
                previewSlots: {},
            },
        };
    }

    export function setFarmSlotItemPreview(state: IGameState, slotId: FarmSlotId, itemId: FarmItemId): IGameState {
        return {
            ...state,
            farmState: {
                ...state.farmState,
                previewSlots: {
                    ...state.farmState.previewSlots,
                    [slotId]: {
                        ...state.farmState.previewSlots[slotId],
                        previewItemId: itemId,
                        isConfirmed: false,
                    },
                },
            },
        };
    }

    export function setConstructionModeEnabled(state: IGameState, constructionModeEnabled: boolean): IGameState {
        return {
            ...state,
            farmState: {
                ...state.farmState,
                constructionModeEnabled,
            },
        };
    }

    export function purchaseFarmArea(state: IGameState, areaId: FarmAreaId): IGameState {
        const areaData = AreaData[areaId];
        const price = areaData.price;

        state = {
            ...state,
            UIState: UIStateHelper.hidePopup(state.UIState, PopupID.FARM_AREA_UNLOCK),
        };

        if (price) {
            if (state.playerState.inventory[RewardType.COINS] > price) {
                state = {
                    ...state,
                    playerState: InventoryStateHelper.spendCoins(state.playerState, price, {
                        forceAnim: true,
                    }),
                };
                state = FarmStateHelper.updateGameState(state, unlockFarmArea(state.playerState.farmState, areaId));
                trackResourceEvent(state, {
                    transactionType: TransactionType.COIN_PURCHASE,
                });
            } else {
                return {
                    ...state,
                    UIState: UIStateHelper.showPopup(
                        state.UIState,
                        isShopSupported(state) ? PopupID.SHOP_MENU : PopupID.NO_COINS_MENU,
                        {}
                    ),
                };
            }
        } else {
            const product = IAPHelper.CACHED_IAP_REWARDS_MAP()[areaData.productId];
            return ContextStateHelper.setContext(state, ContextType.PURCHASE_PRODUCT, { product });
        }

        return state;
    }

    export function purchasePremiumItem(state: IGameState, productId: string): IGameState {
        const product = getIAPRewards().find(iap => iap.id === productId);
        return ContextStateHelper.setContext(state, ContextType.PURCHASE_PRODUCT, { product });
    }

    export function unlockFarmArea(state: IFarmPlayerState, areaId: FarmAreaId): IFarmPlayerState {
        return {
            ...state,
            unlockedAreas: state.unlockedAreas.includes(areaId)
                ? state.unlockedAreas
                : [...state.unlockedAreas, areaId],
        };
    }

    export function receiveMiniGameReward(state: IGameState, rewards: IRewardData[]): IGameState {
        state = {
            ...state,
            playerState: InventoryStateHelper.receiveRewards(state.playerState, rewards, {
                queueTask: true,
            }),
        };

        trackResourceEvent(state, {
            transactionType: TransactionType.REWARD,
            tActionType: TActionType.FEEDING_CHARLY,
        });

        // Update pet feeding timer.
        state = TimerStateHelper.setTimer(state, TimerType.FARM_MINI_GAME, DateHelper.now);

        return state;
    }

    export function clearFarmAreas(state: IFarmPlayerState): IFarmPlayerState {
        return {
            ...state,
            unlockedAreas: initialFarmPlayerState.unlockedAreas,
        };
    }

    /**
     * Adds item to the array of rewarded items in playerState and sets item unlocked
     */
    export function rewardNewItem(state: IPlayerState, itemId: FarmItemId): IPlayerState {
        if (!state.decoItemsRewarded.includes(itemId)) {
            state = {
                ...state,
                decoItemsRewarded: [...state.decoItemsRewarded, itemId],
            };
        }
        return state;
    }

    export function alreadyHasItem(state: IPlayerState, itemId: FarmItemId): boolean {
        return state.decoItemsRewarded.includes(itemId);
    }

    export function updateGameState(state: IGameState, farmState: IFarmPlayerState): IGameState {
        return {
            ...state,
            playerState: {
                ...state.playerState,
                farmState,
            },
        };
    }

    export function getVisibleItems(state: IGameState, includeNextItems: boolean, includeIAPItems: boolean = false) {
        return CACHED_FARM_ITEM_DATA().filter(itemData => {
            return (
                FarmStateHelper.isFarmItemUnlocked(state, itemData.itemId) ||
                (includeNextItems && FarmStateHelper.isNextItem(state, itemData.itemId)) ||
                // Only show non-owned IAP items if IAP is supported on the current platform.
                (includeIAPItems && FarmStateHelper.isIAPFarmItem(itemData.itemId) && state.platform.IAPSupport)
            );
        });
    }

    /**
     * Get all items by the specified slot type.
     * @param slotType
     * @returns
     */
    export function getAvailableItemsBySlotType(slotType: FarmSlotType): FarmItemId[] {
        return getFarmItemsBySlotType(slotType);
    }

    /**
     * Show unseen notification for tab if item in tab is not in `decoItemsSeen` player property.
     * @param items
     * @param decoItemsSeen
     */
    export function areAnyItemsUnseen(items: FarmItemId[], decoItemsSeen: FarmItemId[]) {
        return items.some(item => !decoItemsSeen.includes(item));
    }

    /**
     * Get premium farm items that are unseen
     * @param decoItemsSeen
     */
    export function getUnseenPremiumItems(decoItemsSeen: FarmItemId[]): FarmItemId[] {
        return CACHED_FARM_ITEM_DATA()
            .filter(item => isIAPFarmItem(item.itemId) && !decoItemsSeen.includes(item.itemId))
            .map(item => item.itemId);
    }

    /**
     * Get the farm items IDs to appear in the specified tab for the selected slot type.
     * Premium items always appear in the 'premium' tab regardless of slot type.
     * @param tab
     * @param slotType
     */
    export function getFarmItemIdsForTab(tab: FarmCatalogueTab, slotType: FarmSlotType): FarmItemId[] {
        return CACHED_FARM_ITEM_DATA()
            .filter(item => item.tab === tab && (slotType ? slotType === item.slotType : true))
            .map(item => item.itemId);
    }

    /**
     * Sort given farm items in order for catalog.
     * @param items
     */
    export function sortFarmItems(items: FarmItemId[]) {
        return items.sort((a, b) => {
            const itemA = getFarmItemById(a);
            const itemB = getFarmItemById(b);

            if (
                itemA.farmItemUnlockType === FarmItemUnlockType.CROP &&
                itemB.farmItemUnlockType === FarmItemUnlockType.CROP
            ) {
                const allFields = getAllFieldData().map(field => field.id);
                const itemAFieldIndex = allFields.indexOf(itemA.fieldId);
                const itemBFieldIndex = allFields.indexOf(itemB.fieldId);

                // Only use field index if they are not equal. This allows the ordering within the same field to be controlled by `tabOrder` or natural index.
                if (itemAFieldIndex !== itemBFieldIndex) {
                    return itemAFieldIndex - itemBFieldIndex;
                }
            }

            // If tab order is the same use the natural index.
            if (itemA.tabOrder === itemB.tabOrder) {
                return CACHED_FARM_ITEM_DATA().indexOf(itemA) - CACHED_FARM_ITEM_DATA().indexOf(itemB);
            }

            return itemA.tabOrder - itemB.tabOrder;
        });
    }

    export function filterUnlockedFarmItems(
        state: IGameState,
        items: FarmItemId[],
        includeNextItems: boolean = false,
        includeIAPItems: boolean = false
    ) {
        return items.filter(
            itemId =>
                // Include unlocked items.
                FarmStateHelper.isFarmItemUnlocked(state, itemId) ||
                // Include future items in 'locked' state.
                (includeNextItems && FarmStateHelper.isNextItem(state, itemId)) ||
                // Only show non-owned IAP items if IAP is supported on the current platform.
                (includeIAPItems && FarmStateHelper.isIAPFarmItem(itemId) && state.platform.IAPSupport)
        );
    }

    export function isFarmItemUnlocked(state: IGameState, itemId: FarmItemId): boolean {
        const item = getFarmItemById(itemId);
        if (!item) { return false; }

        switch (item.farmItemUnlockType) {
            case FarmItemUnlockType.CROP:
                return state.playerState.fields.some(fieldId => getFieldNameFromId(fieldId) === item?.fieldId);
            case FarmItemUnlockType.WORLD:
                return state.playerState.maxWorld >= getFarmItemWorldUnlockRequirement(itemId);
            case FarmItemUnlockType.PREMIUM:
                // Due to the incorrect farm item IDs stored in `IAPRewards.json` the `ProductId.XMAS_2021_OFFER` farm items
                // were not added to `state.playerState.decoItemsRewarded` for users who purchased this product.
                // To correct this these farm items are assumed to have been rewarded if the product was purchased:
                const xmasItems: FarmItemId[] = ['house_xmas', 'fence_xmas'];
                if (xmasItems.includes(itemId)) {
                    return IAPStateHelper.hasPurchasedProduct(state, ProductId.XMAS_2021_OFFER);
                }

                return state.playerState.decoItemsRewarded.includes(itemId);
        }
    }

    /**
     * Determine if given farm item is to be shown as a locked "next" item in the farm catalog UI.
     * Crop/field-unlocked items check for items belonging to the next field - if any.
     * World-unlocked items check for items belonging to the next world - if any.
     * Premium items do not show locked or "next" items to be unlocked.
     * @param state
     * @param itemId
     */
    export function isNextItem(state: IGameState, itemId: FarmItemId): boolean {
        const item = getFarmItemById(itemId);
        if (!item) { return false; }

        switch (item.farmItemUnlockType) {
            case FarmItemUnlockType.CROP:
                return item.fieldId === getNextField(state, item);
            case FarmItemUnlockType.WORLD:
                const itemWorldNumberUnlock = getFarmItemWorldUnlockRequirement(itemId);
                const currentWorldNumber = state.playerState.maxWorld;
                return itemWorldNumberUnlock === currentWorldNumber + 1;
            case FarmItemUnlockType.PREMIUM:
                return false;
        }
    }

    export function isIAPFarmItem(itemId: FarmItemId): boolean {
        const item = getFarmItemById(itemId);
        return (
            item?.farmItemUnlockType === FarmItemUnlockType.PREMIUM &&
            item?.productId !== undefined &&
            item?.productId !== null
        );
    }

    /**
     * Get the next field given a current field and farm item type.
     *
     * @param state
     * @param item
     */
    function getNextField(state: IGameState, item: FarmItemData) {
        if (item.farmItemUnlockType !== FarmItemUnlockType.CROP) return null;

        const allFields = WorldDataHelper.getAllFieldData();

        const farmItemsByField = state.playerState.fields.filter(fieldId =>
            getFarmItemsByField(getFieldNameFromId(fieldId)).some(
                farmItem => farmItem.farmItemType === item.farmItemType
            )
        );

        // Filter all fields by those that contain at least one farm item of the same type.
        const itemTypeFields = allFields.filter(field =>
            getFarmItemsByField(getFieldNameFromId(field.id)).some(
                farmItem => farmItem.farmItemType === item.farmItemType
            )
        );

        // Initialize to -1 in case no `current` field is found (first tree field may not be unlocked but we want to return first tree item).
        let currentFieldByItemTypeIndex = -1;

        if (farmItemsByField.length > 0) {
            // Get most recently unlocked field that contains an item of the same type.
            const currentFieldByType = farmItemsByField.pop();

            // Then find the index of the current field in that list (-1 if not found).
            // TODO: `fieldName` === `IFieldData.id` I guess?
            currentFieldByItemTypeIndex = itemTypeFields.findIndex(
                field => getFieldNameFromId(field.id) === getFieldNameFromId(currentFieldByType)
            );
        }

        // Check next field exists (does not exist near end of all worlds).
        if (currentFieldByItemTypeIndex < itemTypeFields.length - 1) {
            // Increment index in filtered fields list by one.
            const nextField = itemTypeFields[currentFieldByItemTypeIndex + 1];
            return getFieldNameFromId(nextField.id);
        }

        // If no more fields exist that contain this item type return null.
        return null;
    }

    export function getFarmItemWorldUnlockRequirement(itemId: FarmItemId): number {
        const item = getFarmItemById(itemId);
        if (item.farmItemUnlockType === FarmItemUnlockType.WORLD) {
            return item.world;
        }
        return 0;
    }

    export function getItemLevelUnlock(state: IGameState, itemId: FarmItemId): number | null {
        const item = getFarmItemById(itemId);

        switch (item.farmItemUnlockType) {
            case FarmItemUnlockType.CROP:
                const allFields = WorldDataHelper.getAllFieldData();
                const nextField = allFields.find(field => field.id === item.fieldId);
                if (nextField !== undefined) {
                    return nextField.requiredLevel[0];
                }
                return null;
            case FarmItemUnlockType.WORLD:
                return WorldDataHelper.maxLevel(Math.max(1, item.world - 1));
        }

        return null;
    }
}
